<template>
    <div class="flex flex-col" style="height: 85vh; max-height: 85vh;">

        <div class="flex-1 overflow-y-auto min-h-0">

            <!-- Mode direct (chantierId fourni en prop) -->
            <div v-if="chantierId || (invoiceId && currentStep === 'facture')">
                <facture-form
                    :invoice-id="invoiceId || null"
                    :client-id="resolvedClientId"
                    :chantier-id="resolvedChantierId"
                    :adresse-id="resolvedAdresseId"
                    @submit="onInvoiceCreated"
                />
            </div>

            <!-- ════ FORMULAIRE CONTEXTE ════ -->
            <div v-else-if="currentStep === 'context'" class="py-6">

    <!-- ── ADRESSE ── -->
    <div class="flex items-center gap-3 px-6 mb-4">
        <div class="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300"
            :class="adresseSelected ? 'bg-orange-500' : 'bg-gray-200'">
            <i class="fas fa-map-marker-alt text-white" style="font-size: 8px"></i>
        </div>
        <span class="text-base font-black tracking-tight transition-colors duration-300"
            :class="adresseSelected ? 'text-gray-900' : 'text-gray-400'">Adresse</span>
        <div class="flex-1 h-px" :class="adresseSelected ? 'bg-orange-200' : 'bg-gray-100'"></div>
    </div>

    <div class="px-6 mb-6">
        <div v-if="!adresseSelected" class="relative">
            <input
                v-model="adresseQuery"
                type="text"
                placeholder="Rechercher une adresse…"
                class="w-full pl-4 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-300 transition-colors bg-white"
                @input="onAdresseInput"
                @blur="hideAdresseSuggestionsDelayed"
            />
            <i v-if="adresseLoading" class="fas fa-spinner fa-spin absolute right-3 top-3.5 text-gray-300 text-sm"></i>
            <div v-if="showAdresseSuggestions && adresseSuggestions.length"
                class="absolute z-30 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div v-for="(item, i) in adresseSuggestions" :key="i"
                    @mousedown.prevent="selectAdresse(item)"
                    class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-50 last:border-0 text-sm transition-colors">
                    <i class="fas fa-map-marker-alt text-orange-300 text-xs flex-shrink-0"></i>
                    <div>
                        <p class="font-medium text-gray-800">{{ item.adresse1 }}</p>
                        <p class="text-xs text-gray-400">{{ item.code_postal }} {{ item.ville }}</p>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl">
            <i class="fas fa-map-marker-alt text-orange-400 text-xs flex-shrink-0"></i>
            <div class="flex-1 min-w-0">
                <span class="text-sm font-semibold text-gray-900">{{ adresseSelected.adresse1 }}</span>
                <span class="text-sm text-gray-400 ml-2">{{ adresseSelected.code_postal }} {{ adresseSelected.ville }}</span>
            </div>
            <button @click="resetAdresse" class="text-xs text-orange-400 hover:text-orange-600 font-semibold transition-colors">
                Modifier
            </button>
        </div>
    </div>

    <!-- ── CLIENT ── -->
    <div v-if="adresseSelected">
        <div class="flex items-center gap-3 px-6 mb-4">
            <div class="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                :class="clientReady ? 'bg-orange-500' : 'bg-orange-200'">
                <i class="fas fa-user text-white" style="font-size: 8px"></i>
            </div>
            <span class="text-base font-black tracking-tight text-gray-900">Client</span>
            <div class="flex-1 h-px" :class="clientReady ? 'bg-orange-200' : 'bg-gray-100'"></div>
        </div>

        <div class="px-6 mb-6">
            <div v-if="loadingSearchClients" class="flex items-center gap-2 text-sm text-gray-400 py-2">
                <i class="fas fa-spinner fa-spin text-orange-300 text-xs"></i> Recherche…
            </div>

            <div v-else class="space-y-2">
                <div
                    v-for="client in clientsFound"
                    :key="client.id"
                    @click="selectedClientId = String(client.id); onClientSelectChange()"
                    class="cursor-pointer rounded-xl border px-4 py-3.5 transition-all duration-300"
                    :class="selectedClientId === String(client.id)
                        ? 'border-orange-300 bg-orange-50/60 opacity-100 shadow-sm shadow-orange-100'
                        : 'border-gray-200 bg-white opacity-50 hover:opacity-100'"
                >
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 transition-colors duration-300"
                            :class="selectedClientId === String(client.id) ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'">
                            {{ clientInitials(client) }}
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-bold text-gray-900 capitalize">{{ clientFullName(client) }}</p>
                            <p class="text-xs text-gray-400">{{ client.telephone || client.email || '—' }}</p>
                        </div>
                        <div class="w-2 h-2 rounded-full transition-all duration-300"
                            :class="selectedClientId === String(client.id) ? 'bg-orange-400' : 'bg-gray-200'"></div>
                    </div>
                    <div v-if="getAddressContext(client.id)" class="flex flex-wrap gap-1.5 mt-2.5 ml-11">
                        <div v-if="getAddressContext(client.id).regie"
                            class="flex items-center gap-1.5 text-xs border rounded-lg px-2 py-0.5 transition-colors duration-300"
                            :class="selectedClientId === String(client.id) ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-gray-50 border-gray-200 text-gray-500'">
                            <i class="fas fa-building" style="font-size:8px"></i>
                            <span class="font-medium">{{ getAddressContext(client.id).regie.nom }}</span>
                            <span class="opacity-60">· {{ getAddressContext(client.id).regie.telephone }}</span>
                        </div>
                        <div v-if="getAddressContext(client.id).locataire"
                            class="flex items-center gap-1.5 text-xs border rounded-lg px-2 py-0.5 transition-colors duration-300"
                            :class="selectedClientId === String(client.id) ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-gray-50 border-gray-200 text-gray-500'">
                            <i class="fas fa-user" style="font-size:8px"></i>
                            <span class="font-medium capitalize">{{ clientFullName(getAddressContext(client.id).locataire) }}</span>
                            <span class="opacity-60">· {{ getAddressContext(client.id).locataire.telephone }}</span>
                        </div>
                    </div>
                </div>

                <div
                    @click="selectedClientId = '__new__'"
                    class="cursor-pointer rounded-xl border px-4 py-3.5 transition-all duration-300"
                    :class="selectedClientId === '__new__'
                        ? 'border-orange-300 bg-orange-50/60 opacity-100'
                        : 'border-dashed border-gray-200 bg-white opacity-50 hover:opacity-100'"
                >
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <i class="fas fa-plus text-gray-400 text-xs"></i>
                        </div>
                        <p class="text-sm font-medium text-gray-500">Nouveau client</p>
                        <div class="ml-auto w-2 h-2 rounded-full transition-all duration-300"
                            :class="selectedClientId === '__new__' ? 'bg-orange-400' : 'bg-gray-200'"></div>
                    </div>
                    <div v-if="selectedClientId === '__new__'" class="mt-3 pt-3 border-t border-orange-100">
                        <client-block title="" icon="" id-prefix="wizard-new-client" :value="newClientData" @input="onNewClientInput" />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- ── CHANTIER ── -->
    <div v-if="adresseSelected && clientReady">
        <div class="flex items-center gap-3 px-6 mb-4">
            <div class="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                :class="selectedChantierId !== '__none__' ? 'bg-orange-500' : 'bg-orange-200'">
                <i class="fas fa-home text-white" style="font-size: 8px"></i>
            </div>
            <span class="text-base font-black tracking-tight text-gray-900">Chantier</span>
            <span class="text-xs text-gray-300 font-normal">(optionnel)</span>
            <div class="flex-1 h-px bg-gray-100"></div>
        </div>

        <div class="px-6">
            <div v-if="loadingChantiers" class="flex items-center gap-2 text-sm text-gray-400 py-2">
                <i class="fas fa-spinner fa-spin text-orange-300 text-xs"></i> Chargement…
            </div>

            <div v-else class="space-y-2">
                <div
                    v-for="chantier in chantiersFound"
                    :key="chantier.id"
                    @click="selectedChantierId = String(chantier.id)"
                    class="relative cursor-pointer rounded-xl border px-4 py-3.5 transition-all duration-300"
                    :class="selectedChantierId === String(chantier.id)
                        ? 'border-orange-300 bg-orange-50/60 opacity-100 shadow-sm shadow-orange-100'
                        : 'border-gray-200 bg-white opacity-50 hover:opacity-100'"
                >
                    <div class="flex items-center gap-3 mb-2">
                        <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                            :class="selectedChantierId === String(chantier.id) ? 'bg-orange-100' : 'bg-gray-100'">
                            <i class="fas fa-home text-sm transition-colors duration-300"
                                :class="selectedChantierId === String(chantier.id) ? 'text-orange-500' : 'text-gray-400'"></i>
                        </div>
                        <div>
                            <p class="text-sm font-bold text-gray-900">{{ chantierSelectLabel(chantier) }}</p>
                            <p class="text-xs text-gray-400">{{ chantier.interventions?.length || 0 }} intervention(s)</p>
                        </div>
                        <div class="ml-auto w-2 h-2 rounded-full transition-all duration-300"
                            :class="selectedChantierId === String(chantier.id) ? 'bg-orange-400' : 'bg-gray-200'"></div>
                    </div>
                    <div v-if="chantier.interventions && chantier.interventions.length" class="space-y-1 ml-11">
                        <div v-for="inv in chantier.interventions.slice(0, 3)" :key="inv.id"
                            class="flex items-center gap-2 text-xs text-gray-500">
                            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ background: inv.statut?.couleur || '#d1d5db' }"></span>
                            <span class="flex-1 truncate">{{ appareilLabel(inv.appareil) }}</span>
                            <span class="text-gray-400">{{ fmtDate(inv.date_intervention) }}</span>
                            <span class="px-1.5 py-0.5 rounded-full font-semibold"
                                :style="{ background: (inv.statut?.couleur || '#9ca3af') + '18', color: inv.statut?.couleur || '#9ca3af' }">
                                {{ inv.statut?.libelle }}
                            </span>
                        </div>
                        <p v-if="chantier.interventions.length > 3" class="text-xs text-gray-300 italic">
                            + {{ chantier.interventions.length - 3 }} autre(s)…
                        </p>
                    </div>
                </div>

                <div
                    @click="selectedChantierId = '__none__'"
                    class="cursor-pointer rounded-xl border px-4 py-3 transition-all duration-300"
                    :class="selectedChantierId === '__none__'
                        ? 'border-orange-300 bg-orange-50/60 opacity-100'
                        : 'border-dashed border-gray-200 bg-white opacity-50 hover:opacity-100'"
                >
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <i class="fas fa-ban text-gray-300 text-xs"></i>
                        </div>
                        <p class="text-sm text-gray-400">Sans chantier</p>
                        <div class="ml-auto w-2 h-2 rounded-full transition-all duration-300"
                            :class="selectedChantierId === '__none__' ? 'bg-orange-400' : 'bg-gray-200'"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>

</div>

            <!-- ════ FACTURE ════ -->
            <div v-else-if="currentStep === 'facture'">
                <div class="flex items-center gap-2 flex-wrap px-6 pt-4 pb-3 border-b border-gray-100">
                    <button @click="goBack" class="text-xs text-orange-500 hover:text-orange-700 font-semibold mr-auto transition-colors">
                        <i class="fas fa-arrow-left mr-1" style="font-size:10px"></i> Modifier
                    </button>
                    <div class="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <i class="fas fa-map-marker-alt text-orange-400" style="font-size:10px"></i>
                        <span>{{ adresseSelected?.adresse1 }}, {{ adresseSelected?.code_postal }} {{ adresseSelected?.ville }}</span>
                    </div>
                    <div class="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <i class="fas fa-user text-orange-400" style="font-size:10px"></i>
                        <span>{{ resolvedClientName }}</span>
                    </div>
                    <div v-if="resolvedChantierId" class="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                        <i class="fas fa-home text-orange-400" style="font-size:10px"></i>
                        <span>{{ chantierSelectLabel(selectedChantierObj) }}</span>
                    </div>
                </div>

                <facture-form
                :invoice-id="invoiceId || null"
                :client-id="resolvedClientId"
                :chantier-id="resolvedChantierId"
                :adresse-id="resolvedAdresseId"
                @submit="onInvoiceCreated"
                />
            </div>

        </div>

        <!-- Footer -->
        <div v-if="!chantierId && currentStep === 'context'" class="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex items-center justify-end">
            <button
                @click="goToFacture"
                :disabled="!canProceed || loadingNext"
                :class="[
                    'flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all',
                    canProceed && !loadingNext
                        ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                ]"
            >
                <i v-if="loadingNext" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-file-alt"></i>
                Créer le document
            </button>
        </div>

    </div>
</template>

<script>
import FactureForm  from './FactureForm.vue'
import ClientBlock  from '../../Client/Components/ClientBlock.vue'

const GOUV_URL = 'https://api-adresse.data.gouv.fr/search/'

export default {
    name: 'FactureWizard',
    components: { FactureForm, ClientBlock },

    props: {
        chantierId: { type: [Number, String], default: null },
        clientId:   { type: [Number, String], default: null },
        adresseId:  { type: [Number, String], default: null }, // ← nouveau (mode direct)
        invoiceId:  { type: [Number, String], default: null },
    },

    emits: ['invoice-created'],

    data() {
        return {
            currentStep: 'context',
            loadingNext: false,

            // Adresse
            adresseQuery:           '',
            adresseSuggestions:     [],
            adresseDebounce:        null,
            adresseLoading:         false,
            showAdresseSuggestions: false,
            adresseSelected:        null,

            // Client
            loadingSearchClients: false,
            clientsFound:         [],
            selectedClientId:     '__new__', // défaut : nouveau client
            newClientData:        {},

            // Chantier
            loadingChantiers:   false,
            chantiersFound:     [],
            selectedChantierId: '__none__',

            // Résolution
            resolvedClientId:   null,
            resolvedChantierId: null,
            resolvedClientName: '',

            resolvedAdresseId: null,

            addressesFound: [],
        }
    },

    computed: {
        selectedClientObj() {
            if (!this.selectedClientId || this.selectedClientId === '__new__') return null
            return this.clientsFound.find(c => String(c.id) === String(this.selectedClientId)) || null
        },

        selectedChantierObj() {
            if (!this.selectedChantierId || this.selectedChantierId === '__none__') return null
            return this.chantiersFound.find(c => String(c.id) === String(this.selectedChantierId)) || null
        },

        clientReady() {
            if (this.selectedClientId === '__new__') return !!this.newClientData?.nom?.trim()
            return !!this.selectedClientId
        },

        canProceed() {
            return !!this.adresseSelected && this.clientReady
        },
    },

    mounted() {
        if (this.chantierId) {
            this.resolvedChantierId = this.chantierId
            this.resolvedClientId   = this.clientId
            this.resolvedAdresseId  = this.adresseId
        } else if (this.invoiceId) {
            // Mode édition : on a déjà tout, on saute le contexte
            this.resolvedClientId   = this.clientId
            this.resolvedChantierId = this.chantierId
            this.resolvedAdresseId  = this.adresseId
            this.currentStep        = 'facture'
        }
    },

    methods: {
        // ─── Adresse ───────────────────────────────────────────
        onAdresseInput() {
            clearTimeout(this.adresseDebounce)
            this.adresseSuggestions = []
            if (this.adresseQuery.length < 3) return
            this.adresseDebounce = setTimeout(() => this.fetchAdresseSuggestions(), 350)
        },

        async fetchAdresseSuggestions() {
            this.adresseLoading = true
            try {
                const r = await fetch(`${GOUV_URL}?q=${encodeURIComponent(this.adresseQuery)}&limit=5`)
                const d = await r.json()
                this.adresseSuggestions = d.features.map(f => ({
                    adresse1:    f.properties.name,
                    code_postal: f.properties.postcode,
                    ville:       f.properties.city,
                    latitude:    f.geometry.coordinates[1],
                    longitude:   f.geometry.coordinates[0],
                }))
                this.showAdresseSuggestions = !!this.adresseSuggestions.length
            } catch (e) {
                console.error(e)
            } finally {
                this.adresseLoading = false
            }
        },

        async selectAdresse(item) {
            this.adresseSelected        = { ...item }
            this.adresseQuery           = `${item.adresse1}, ${item.code_postal} ${item.ville}`
            this.adresseSuggestions     = []
            this.showAdresseSuggestions = false
            this.selectedClientId       = '__new__'
            this.selectedChantierId     = '__none__'
            this.clientsFound           = []
            this.chantiersFound         = []
            this.newClientData          = {}
            this.resolvedAdresseId      = null   // ← nouveau

            await this.loadClientsAtAddress()
        },

        resetAdresse() {
            this.adresseSelected    = null
            this.adresseQuery       = ''
            this.clientsFound       = []
            this.chantiersFound     = []
            this.selectedClientId   = '__new__'
            this.selectedChantierId = '__none__'
            this.newClientData      = {}
        },

        hideAdresseSuggestionsDelayed() {
            setTimeout(() => { this.showAdresseSuggestions = false }, 200)
        },

        // ─── Client ────────────────────────────────────────────
        async loadClientsAtAddress() {
            this.loadingSearchClients = true
            try {
                const res = await this.$request.post('/clients/search-geocode', {
                    longitude: this.adresseSelected.longitude,
                    latitude:  this.adresseSelected.latitude,
                })

                this.addressesFound = Array.isArray(res) ? res : []
                this.clientsFound   = this.addressesFound.map(a => a.proprietaire).filter(Boolean)

                if (this.clientsFound.length === 1) {
                    this.selectedClientId = String(this.clientsFound[0].id)
                    this.applyAddressContext(this.clientsFound[0].id)
                    await this.loadChantiersForClient(this.clientsFound[0].id)
                }
            } catch (e) {
                console.error(e)
                this.clientsFound = []
            } finally {
                this.loadingSearchClients = false
            }
        },

        async onClientSelectChange() {
            this.selectedChantierId = '__none__'
            this.chantiersFound     = []
            if (this.selectedClientId && this.selectedClientId !== '__new__') {
                this.applyAddressContext(this.selectedClientId)
                await this.loadChantiersForClient(this.selectedClientId)
            } else {
                this.resolvedAdresseId = null
            }
        },


        onNewClientInput(val) {
            this.newClientData = { ...val }
        },

        clientFullName(client) {
            return [client.prenom, client.nom].filter(Boolean).join(' ') || '—'
        },

        clientInitials(client) {
            return ((client.prenom || '').charAt(0) + (client.nom || '').charAt(0)).toUpperCase() || '?'
        },

        // ─── Chantier ──────────────────────────────────────────
        async loadChantiersForClient(clientId) {
            this.loadingChantiers = true
            try {
                const res = await this.$request.post('/chantiers/list-by-client', { client_id: clientId })
                this.chantiersFound = res?.data || res || []
            } catch (e) {
                console.error(e)
                this.chantiersFound = []
            } finally {
                this.loadingChantiers = false
            }
        },

        // Label du select : "JJ/MM/YYYY - Statut"
        chantierSelectLabel(chantier) {
            if (!chantier) return '—'
            const dates = (chantier.interventions || [])
                .map(i => i.date_intervention)
                .filter(Boolean)
                .sort()
            const date = dates.length
                ? this.fmtDate(dates[dates.length - 1])
                : this.fmtDate(chantier.created_at)
            const statut = chantier.statut?.libelle || ''
            return `${date}${statut ? ' - ' + statut : ''}`
        },

        appareilLabel(appareil) {
            if (!appareil) return 'Appareil inconnu'
            const typeMap = {
                pac_air_eau:  'PAC Air/Eau',
                pac_eau_eau:  'PAC Eau/Eau',
                pac_air_air:  'PAC Air/Air',
                clim_air_air: 'Climatisation',
                chaudiere:    'Chaudière',
                ballon:       'Ballon',
                vmc:          'VMC',
            }
            return [typeMap[appareil.type] || appareil.type, appareil.marque, appareil.model]
                .filter(Boolean).join(' ')
        },

        fmtDate(d) {
            if (!d) return '—'
            return new Date(d).toLocaleDateString('fr-FR')
        },

        // ─── Navigation ────────────────────────────────────────
        async goToFacture() {
            if (!this.canProceed) return
            this.loadingNext = true
            try {
                // 1. Résoudre le client
                if (this.selectedClientId !== '__new__') {
                    this.resolvedClientId   = this.selectedClientId
                    this.resolvedClientName = this.clientFullName(this.selectedClientObj)
                } else {
                    const res = await this.$request.post('/clients', {
                        ...this.newClientData,
                        adresse1:    this.adresseSelected?.adresse1,
                        code_postal: this.adresseSelected?.code_postal,
                        ville:       this.adresseSelected?.ville,
                        latitude:    this.adresseSelected?.latitude,
                        longitude:   this.adresseSelected?.longitude,
                    })
                    const client = res.client          // ← api.client après unwrap par $request
                    this.resolvedClientId   = client.id
                    this.resolvedClientName = this.clientFullName(client)
                }

                // 2. Résoudre l'adresse — maintenant qu'on a le client_id
                const adresseRes = await this.$request.post('/client-adresses/find-or-create', {
                    client_id:   this.resolvedClientId,
                    adresse1:    this.adresseSelected.adresse1,
                    code_postal: this.adresseSelected.code_postal,
                    ville:       this.adresseSelected.ville,
                    latitude:    this.adresseSelected.latitude,
                    longitude:   this.adresseSelected.longitude,
                })
                this.resolvedAdresseId = adresseRes.id ?? adresseRes.api?.id ?? null

                // 3. Résoudre le chantier
                this.resolvedChantierId = this.selectedChantierId !== '__none__'
                    ? this.selectedChantierId
                    : null

                this.currentStep = 'facture'
            } catch (e) {
                console.error(e)
                this.$toast?.error('Une erreur est survenue.')
            } finally {
                this.loadingNext = false
            }
        },

        goBack() {
            this.currentStep        = 'context'
            this.resolvedClientId   = null
            this.resolvedChantierId = null
        },

        onInvoiceCreated(invoice) {
            this.$emit('invoice-created', invoice)
        },

        applyAddressContext(clientId) {
            const addr = this.addressesFound.find(a => a.proprietaire?.id === Number(clientId))
            if (!addr) return
            this.resolvedAdresseId = addr.id
        },

        getAddressContext(clientId) {
            return this.addressesFound.find(a => a.proprietaire?.id === Number(clientId)) || null
        },
    },
}
</script>